// src/pages/BlogEditor.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, GripVertical, Save, Eye,
  Type, Quote, List, Code, Image, AlertCircle, Minus,
} from "lucide-react";
import { supabase, SUPABASE_READY } from "../utils/supabase";
import { useAdminAuth } from "../hooks/useAdminAuth";

// ── Block type configs ────────────────────────────────────────────────────────
const BLOCK_TYPES = [
  { type: "p",          icon: Type,         label: "Paragraph" },
  { type: "h2",         icon: Type,         label: "Heading 2" },
  { type: "h3",         icon: Type,         label: "Heading 3" },
  { type: "blockquote", icon: Quote,        label: "Quote" },
  { type: "callout",    icon: AlertCircle,  label: "Callout" },
  { type: "list",       icon: List,         label: "List" },
  { type: "code",       icon: Code,         label: "Code" },
  { type: "image",      icon: Image,        label: "Image" },
  { type: "divider",    icon: Minus,        label: "Divider" },
];

const COLORS = ["default", "muted", "green", "red", "blue", "yellow", "purple"];

const COLOR_DOT = {
  default: "bg-foreground",
  muted:   "bg-muted-foreground",
  green:   "bg-emerald-500",
  red:     "bg-red-500",
  blue:    "bg-blue-500",
  yellow:  "bg-yellow-400",
  purple:  "bg-violet-500",
};

const newBlock = (type) => {
  const base = { type, id: Date.now().toString(36) + Math.random().toString(36).slice(2) };
  switch (type) {
    case "p":          return { ...base, text: "", color: "default" };
    case "h2":         return { ...base, text: "", color: "default" };
    case "h3":         return { ...base, text: "", color: "default" };
    case "blockquote": return { ...base, text: "", accentColor: "default" };
    case "callout":    return { ...base, text: "", color: "blue", icon: "→" };
    case "list":       return { ...base, items: [""], markerColor: "default" };
    case "code":       return { ...base, text: "", lang: "js" };
    case "image":      return { ...base, src: "", alt: "", caption: "", size: "lg" };
    case "divider":    return { ...base };
    default:           return base;
  }
};

// ── Individual block editors ──────────────────────────────────────────────────
const ColorPicker = ({ value, onChange, label = "Color" }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">{label}:</span>
    {COLORS.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        title={c}
        className={`w-4 h-4 rounded-full border-2 transition-all ${COLOR_DOT[c]} ${value === c ? "border-foreground scale-125" : "border-transparent"}`}
      />
    ))}
  </div>
);

const TextareaField = ({ value, onChange, placeholder, rows = 3, mono = false }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={`w-full bg-transparent border hairline rounded px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 resize-none ${mono ? "font-mono text-xs" : ""}`}
  />
);

const BlockEditor = ({ block, onChange, onDelete, index, total }) => {
  const update = (patch) => onChange({ ...block, ...patch });

  const header = (
    <div className="flex items-center gap-2 mb-3">
      <GripVertical size={14} className="text-muted-foreground flex-shrink-0" />
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex-1">
        {BLOCK_TYPES.find((b) => b.type === block.type)?.label || block.type}
      </span>
      <button
        type="button"
        onClick={onDelete}
        className="text-muted-foreground hover:text-red-500 transition-colors"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );

  switch (block.type) {
    case "p":
    case "h2":
    case "h3":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <TextareaField value={block.text} onChange={(v) => update({ text: v })} placeholder="Text…" rows={block.type === "p" ? 4 : 2} />
          <ColorPicker value={block.color} onChange={(v) => update({ color: v })} />
        </div>
      );

    case "blockquote":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <TextareaField value={block.text} onChange={(v) => update({ text: v })} placeholder="Quote text…" rows={3} />
          <ColorPicker value={block.accentColor} onChange={(v) => update({ accentColor: v })} label="Accent" />
        </div>
      );

    case "callout":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <div className="flex gap-2">
            <input
              value={block.icon || ""}
              onChange={(e) => update({ icon: e.target.value })}
              placeholder="Icon (→ ⚠ ✓ ✦)"
              className="w-24 bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
            />
            <TextareaField value={block.text} onChange={(v) => update({ text: v })} placeholder="Callout text…" rows={2} />
          </div>
          <ColorPicker value={block.color} onChange={(v) => update({ color: v })} />
        </div>
      );

    case "list":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <div className="space-y-2">
            {(block.items || [""]).map((item, i) => {
              const isObj = typeof item === "object";
              const text  = isObj ? item.text : item;
              const color = isObj ? item.color : "default";
              return (
                <div key={i} className="flex gap-2 items-start">
                  <span className="font-mono text-xs text-muted-foreground mt-2.5 flex-shrink-0">0{i + 1}</span>
                  <input
                    value={text}
                    onChange={(e) => {
                      const items = [...(block.items || [])];
                      items[i] = isObj ? { ...item, text: e.target.value } : e.target.value;
                      update({ items });
                    }}
                    placeholder={`Item ${i + 1}…`}
                    className="flex-1 bg-transparent border hairline rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
                  />
                  {/* item color */}
                  <select
                    value={color}
                    onChange={(e) => {
                      const items = [...(block.items || [])];
                      items[i] = { text, color: e.target.value };
                      update({ items });
                    }}
                    className="bg-background border hairline rounded px-2 py-1.5 text-xs font-mono"
                  >
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const items = (block.items || []).filter((_, j) => j !== i);
                      update({ items: items.length ? items : [""] });
                    }}
                    className="text-muted-foreground hover:text-red-500 mt-1.5"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => update({ items: [...(block.items || []), ""] })}
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <Plus size={11} /> Add item
          </button>
          <ColorPicker value={block.markerColor} onChange={(v) => update({ markerColor: v })} label="Marker" />
        </div>
      );

    case "code":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <input
            value={block.lang || ""}
            onChange={(e) => update({ lang: e.target.value })}
            placeholder="Language (js, sql, java, bash…)"
            className="w-48 bg-transparent border hairline rounded px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
          <TextareaField value={block.text} onChange={(v) => update({ text: v })} placeholder="Code…" rows={8} mono />
        </div>
      );

    case "image":
      return (
        <div className="border hairline rounded-md p-4 space-y-3">
          {header}
          <input
            value={block.src || ""}
            onChange={(e) => update({ src: e.target.value })}
            placeholder="Image URL (https://…)"
            className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
          <div className="flex gap-2">
            <input
              value={block.alt || ""}
              onChange={(e) => update({ alt: e.target.value })}
              placeholder="Alt text"
              className="flex-1 bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
            />
            <select
              value={block.size || "lg"}
              onChange={(e) => update({ size: e.target.value })}
              className="bg-background border hairline rounded px-3 py-2 text-sm font-mono"
            >
              {["sm", "md", "lg", "full"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input
            value={block.caption || ""}
            onChange={(e) => update({ caption: e.target.value })}
            placeholder="Caption (optional)"
            className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
          {block.src && (
            <img src={block.src} alt={block.alt} className="max-h-40 rounded border hairline object-cover" />
          )}
        </div>
      );

    case "divider":
      return (
        <div className="border hairline rounded-md p-4">
          {header}
          <div className="border-t hairline" />
        </div>
      );

    default:
      return null;
  }
};

// ── Blog Editor page ──────────────────────────────────────────────────────────
const BlogEditor = () => {
  const { isAdmin } = useAdminAuth();
  const navigate    = useNavigate();
  const { id }      = useParams(); // "new" or existing slug
  const isNew       = !id || id === "new";

  const [title,    setTitle]    = useState("");
  const [excerpt,  setExcerpt]  = useState("");
  const [category, setCategory] = useState("Backend");
  const [tags,     setTags]     = useState("");
  const [readTime, setReadTime] = useState("5 min read");
  const [heroSrc,  setHeroSrc]  = useState("");
  const [heroAlt,  setHeroAlt]  = useState("");
  const [blocks,   setBlocks]   = useState([newBlock("p")]);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) navigate("/journal");
  }, [isAdmin, navigate]);

  // Load existing post
  useEffect(() => {
    if (!isNew && SUPABASE_READY && id) {
      supabase.from("blog_posts").select("*").eq("slug", id).single()
        .then(({ data }) => {
          if (data) {
            setTitle(data.title || "");
            setExcerpt(data.excerpt || "");
            setCategory(data.category || "Backend");
            setTags((data.tags || []).join(", "));
            setReadTime(data.read_time || "5 min read");
            setHeroSrc(data.hero_image?.src || "");
            setHeroAlt(data.hero_image?.alt || "");
            setBlocks(data.content || [newBlock("p")]);
          }
        });
    }
  }, [id, isNew]);

  const addBlock = (type) => {
    setBlocks((prev) => [...prev, newBlock(type)]);
    setShowBlockMenu(false);
  };

  const updateBlock = (index, updated) => {
    setBlocks((prev) => prev.map((b, i) => i === index ? updated : b));
  };

  const deleteBlock = (index) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const generateSlug = (t) =>
    t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleSave = async (published = true) => {
    if (!title.trim()) return alert("Title is required.");
    if (!SUPABASE_READY) return alert("Supabase not configured.");

    setSaving(true);
    const slug = isNew ? generateSlug(title) : id;

    // Strip internal editor IDs from blocks before saving
    const cleanBlocks = blocks.map(({ id: _id, ...rest }) => rest);

    const payload = {
      slug,
      title:      title.trim(),
      excerpt:    excerpt.trim(),
      category:   category.trim(),
      tags:       tags.split(",").map((t) => t.trim()).filter(Boolean),
      read_time:  readTime.trim(),
      hero_image: heroSrc ? { src: heroSrc, alt: heroAlt } : null,
      content:    cleanBlocks,
      published,
      updated_at: new Date().toISOString(),
    };

    const { error } = isNew
      ? await supabase.from("blog_posts").insert({ ...payload, created_at: new Date().toISOString() })
      : await supabase.from("blog_posts").update(payload).eq("slug", id);

    setSaving(false);
    if (error) { alert("Save failed: " + error.message); return; }
    setSaved(true);
    setTimeout(() => { setSaved(false); navigate(`/journal/${slug}`); }, 800);
  };

  if (!isAdmin) return null;

  return (
    <div>
      {/* ── Header ── */}
      <div className="border-b hairline sticky top-0 bg-background z-30">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/journal")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest flex-1">
            {isNew ? "New Post" : "Edit Post"}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2 border hairline rounded-md font-mono text-[11px] uppercase tracking-widest hover:bg-muted transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              <Eye size={12} /> Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="px-4 py-2 bg-foreground text-background rounded-md font-mono text-[11px] uppercase tracking-widest hover:opacity-80 transition-opacity disabled:opacity-40 flex items-center gap-2"
            >
              <Save size={12} /> {saving ? "Saving…" : saved ? "Saved ✓" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-10 space-y-6">
        {/* ── Meta fields ── */}
        <div className="border hairline rounded-md p-6 space-y-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Post Metadata</p>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title…"
            className="w-full bg-transparent border-b hairline py-2 text-2xl font-display focus:outline-none focus:border-foreground transition-colors"
          />

          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Short excerpt shown in listings…"
            rows={2}
            className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 resize-none"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest block mb-1">Category</label>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Backend"
                className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest block mb-1">Read Time</label>
              <input
                value={readTime}
                onChange={(e) => setReadTime(e.target.value)}
                placeholder="5 min read"
                className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>
            <div className="col-span-2">
              <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest block mb-1">Tags (comma-separated)</label>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Node.js, Performance, AWS"
                className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest block mb-1">Hero Image URL</label>
              <input
                value={heroSrc}
                onChange={(e) => setHeroSrc(e.target.value)}
                placeholder="https://…"
                className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest block mb-1">Hero Alt Text</label>
              <input
                value={heroAlt}
                onChange={(e) => setHeroAlt(e.target.value)}
                placeholder="Description of image"
                className="w-full bg-transparent border hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/20"
              />
            </div>
          </div>
        </div>

        {/* ── Content blocks ── */}
        <div className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Content Blocks</p>

          {blocks.map((block, i) => (
            <BlockEditor
              key={block.id || i}
              block={block}
              index={i}
              total={blocks.length}
              onChange={(updated) => updateBlock(i, updated)}
              onDelete={() => deleteBlock(i)}
            />
          ))}
        </div>

        {/* ── Add block ── */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowBlockMenu((v) => !v)}
            className="w-full flex items-center justify-center gap-2 py-3 border hairline border-dashed rounded-md font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-foreground/50 transition-all"
          >
            <Plus size={14} /> Add Block
          </button>

          {showBlockMenu && (
            <div className="absolute bottom-full mb-2 left-0 right-0 border hairline rounded-md bg-background shadow-xl z-20 grid grid-cols-3 gap-1 p-2">
              {BLOCK_TYPES.map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => addBlock(type)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded hover:bg-muted transition-colors text-left"
                >
                  <Icon size={13} className="text-muted-foreground flex-shrink-0" />
                  <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;